import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Avatar, Button, Chip, Snackbar, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, 
  FormControl, InputLabel, Select, MenuItem, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Paper, IconButton, Divider
} from '@mui/material';
import { 
  Description, 
  LocalShipping, 
  Print, 
  Visibility, 
  CheckCircle, 
  Add, 
  ChevronRight,
  Assignment,
  Edit
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const Guias = () => {
  const queryClient = useQueryClient();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  const [nuevaGuiaDialog, setNuevaGuiaDialog] = useState(false);
  const [verGuiasDialog, setVerGuiasDialog] = useState(false);
  const [detallesDialog, setDetallesDialog] = useState(false);
  const [selectedGuia, setSelectedGuia] = useState(null);
  const [filterType, setFilterType] = useState('todas');
  
  const [guiaForm, setGuiaForm] = useState({ tipo: 'movimiento', origen: '', destino: '', validez: new Date().toISOString().split('T')[0], estado: 'emitida' });

  const { data: guiasData, isLoading } = useQuery('guias', async () => {
    const res = await axios.get('/api/guias');
    return res.data;
  });

  const guias = Array.isArray(guiasData) ? guiasData : (guiasData?.guias || []);

  const createGuia = useMutation(async (data) => {
    const code = `G-${Math.floor(100000 + Math.random() * 900000)}`;
    await axios.post('/api/guias', { ...data, codigo_guia: code, fecha_emision: new Date().toISOString().split('T')[0] });
  }, { onSuccess: () => { queryClient.invalidateQueries('guias'); setNuevaGuiaDialog(false); showMessage('✅ Guía oficial emitida'); } });

  const updateGuia = useMutation(async (data) => {
    await axios.put(`/api/guias/${data.id}`, data);
  }, { onSuccess: () => { queryClient.invalidateQueries('guias'); setDetallesDialog(false); showMessage('✅ Guía actualizada'); } });

  const showMessage = (msg) => { setSnackbarMessage(msg); setOpenSnackbar(true); };

  const { data: explotacionesData } = useQuery('explotaciones', async () => {
    const res = await axios.get('/api/explotaciones');
    return res.data;
  });

  const explotaciones = Array.isArray(explotacionesData) ? explotacionesData : [];

  const filteredGuias = guias.filter(g => filterType === 'todas' || g.tipo === filterType);
  const guiasEmitidasData = guias.filter(g => g.estado === 'completada' || g.estado === 'finalizada');
  const guiasEnCursoData = guias.filter(g => g.estado === 'emitida' || g.estado === 'programada' || g.estado === 'en transito' || g.estado === 'en curso' || g.estado === 'aprobada');

  const handlePrint = () => { window.print(); };
  const handleVerDetalles = (guia) => { setSelectedGuia(guia); setDetallesDialog(true); };

  if (isLoading) return <Box sx={{ p: 4, mt: 8, textAlign: 'center' }}><Typography variant="h6">Sincronizando registros...</Typography></Box>;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: 8, bgcolor: 'var(--bg-app)', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }} className="no-print">
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--navy-main)' }}>Guías y Tránsitos</Typography>
          <Typography variant="body1" sx={{ color: 'var(--slate-light)' }}>Documentación oficial y trazabilidad logística.</Typography>
        </Box>
        <Button 
          className="luxury-button luxury-button-primary"
          variant="contained" 
          startIcon={<Add />}
          onClick={() => { setGuiaForm({ tipo: 'movimiento', origen: '', destino: '', validez: new Date().toISOString().split('T')[0], estado: 'emitida' }); setNuevaGuiaDialog(true); }}
        >
          Nueva Guía
        </Button>
      </Box>
      
      {/* KPIs Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }} className="no-print">
        {[
          { label: 'Emitidas / Listas', value: guiasEmitidasData.length, color: 'var(--emerald-main)', icon: <CheckCircle /> },
          { label: 'Transportes Activos', value: guiasEnCursoData.length, color: 'var(--alert-gold)', icon: <LocalShipping /> },
          { label: 'Registro Maestro', value: guias.length, color: 'var(--navy-main)', icon: <Assignment /> },
        ].map((s, i) => (
          <Grid item xs={12} md={4} key={i}>
            <Card className="premium-card">
              <CardContent sx={{ p: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: `${s.color}15`, color: s.color, width: 56, height: 56 }}>{s.icon}</Avatar>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 800 }}>{s.value}</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--slate-light)', textTransform: 'uppercase' }}>{s.label}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      <Grid container spacing={4} className="no-print">
        <Grid item xs={12} lg={6}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Tránsitos Finalizados</Typography>
          <Card className="premium-card">
            <Box sx={{ p: 1 }}>
              {guiasEmitidasData.slice(0, 5).map((guia) => (
                <Box key={guia.id} onClick={() => handleVerDetalles(guia)} sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'var(--slate-bg)' }, borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Description color="primary" />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{guia.codigo_guia}</Typography>
                      <Typography variant="caption" sx={{ color: 'var(--slate-light)' }}>{guia.destino}</Typography>
                    </Box>
                  </Box>
                  <ChevronRight sx={{ color: 'var(--border-color)' }} />
                </Box>
              ))}
              {guiasEmitidasData.length === 0 && <Box sx={{ p: 4, textAlign: 'center', color: 'var(--slate-light)' }}>Sin tránsitos finalizados.</Box>}
            </Box>
            <Button fullWidth onClick={() => setVerGuiasDialog(true)} sx={{ py: 2, fontWeight: 800 }}>Ver Registro Completo</Button>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>En Ruta / Programadas</Typography>
          <Card className="premium-card">
            <Box sx={{ p: 1 }}>
              {guiasEnCursoData.slice(0, 5).map((guia) => (
                <Box key={guia.id} onClick={() => handleVerDetalles(guia)} sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'var(--slate-bg)' }, borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LocalShipping sx={{ color: 'var(--alert-gold)' }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{guia.codigo_guia}</Typography>
                      <Chip label={guia.status || guia.estado} size="small" sx={{ height: 18, fontSize: '10px', textTransform: 'uppercase' }} />
                    </Box>
                  </Box>
                  <ChevronRight sx={{ color: 'var(--border-color)' }} />
                </Box>
              ))}
              {guiasEnCursoData.length === 0 && <Box sx={{ p: 4, textAlign: 'center', color: 'var(--slate-light)' }}>No hay guías activas en curso.</Box>}
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Emitir Guía */}
      <Dialog open={nuevaGuiaDialog} onClose={() => setNuevaGuiaDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Emisión de Nueva Guía</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Movimiento</InputLabel>
                <Select value={guiaForm.tipo} label="Tipo de Movimiento" onChange={(e) => setGuiaForm({...guiaForm, tipo: e.target.value})}>
                  <MenuItem value="movimiento">Traslado Nacional</MenuItem>
                  <MenuItem value="venta">Venta / Transacción</MenuItem>
                  <MenuItem value="exportacion">Exportación Estratégica</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Origen (Explotación)</InputLabel>
                <Select value={guiaForm.origen} label="Origen (Explotación)" onChange={(e) => setGuiaForm({...guiaForm, origen: e.target.value})}>
                  <MenuItem value="Explotación Demo">Mi Explotación (Principal)</MenuItem>
                  {explotaciones.map(ex => <MenuItem key={ex.id} value={ex.nombre}>{ex.nombre} ({ex.codigo_rega})</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Destino</InputLabel>
                <Select value={guiaForm.destino} label="Destino" onChange={(e) => setGuiaForm({...guiaForm, destino: e.target.value})}>
                  <MenuItem value="Matadero Central">Matadero Central</MenuItem>
                  <MenuItem value="Mercado Central">Mercado Central</MenuItem>
                  {explotaciones.filter(ex => ex.nombre !== guiaForm.origen).map(ex => <MenuItem key={ex.id} value={ex.nombre}>{ex.nombre}</MenuItem>)}
                  <MenuItem value="Otro">Otro Destino (Manual)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {guiaForm.destino === 'Otro' && (
              <Grid item xs={12}>
                <TextField fullWidth label="Especificar Destino Manual" onChange={(e) => setGuiaForm({...guiaForm, destino: e.target.value})} />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField fullWidth type="date" label="Vencimiento de Guía" InputLabelProps={{ shrink: true }} value={guiaForm.validez} onChange={(e) => setGuiaForm({...guiaForm, validez: e.target.value})} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setNuevaGuiaDialog(false)}>Cancelar</Button>
          <Button variant="contained" className="luxury-button luxury-button-primary" onClick={() => createGuia.mutate(guiaForm)}>Emitir Documento</Button>
        </DialogActions>
      </Dialog>

      {/* Detalles Guía */}
      <Dialog open={detallesDialog} onClose={() => setDetallesDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
        {selectedGuia && (
          <Box sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{selectedGuia.codigo_guia}</Typography>
              <Chip label={selectedGuia.estado} color="primary" sx={{ fontWeight: 800 }} />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--slate-light)' }}>ORIGEN</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>{selectedGuia.origen || 'Sede Principal'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--slate-light)' }}>DESTINO</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>{selectedGuia.destino}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--slate-light)' }}>FECHA EMISIÓN</Typography>
                <Typography variant="body2">{selectedGuia.fecha_emision}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--slate-light)' }}>VALIDEZ</Typography>
                <Typography variant="body2">{selectedGuia.validez}</Typography>
              </Grid>
              <Grid item xs={12} sx={{ mt: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Actualizar Estado</InputLabel>
                  <Select value={selectedGuia.estado} label="Actualizar Estado" onChange={(e) => updateGuia.mutate({...selectedGuia, estado: e.target.value})}>
                    <MenuItem value="emitida">Emitida</MenuItem>
                    <MenuItem value="en transito">En Tránsito</MenuItem>
                    <MenuItem value="completada">Completada</MenuItem>
                    <MenuItem value="cancelada">Cancelada</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
              <Button fullWidth variant="outlined" onClick={handlePrint} startIcon={<Print />}>Imprimir</Button>
              <Button fullWidth variant="contained" className="luxury-button luxury-button-primary" onClick={() => setDetallesDialog(false)}>Cerrar</Button>
            </Box>
          </Box>
        )}
      </Dialog>

      {/* Todos los Registros */}
      <Dialog open={verGuiasDialog} onClose={() => setVerGuiasDialog(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Registro Maestro de Documentación</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel>Filtrar por Categoría</InputLabel>
              <Select value={filterType} label="Filtrar por Categoría" onChange={(e) => setFilterType(e.target.value)}>
                <MenuItem value="todas">Todas las Guías</MenuItem>
                <MenuItem value="movimiento">Movimientos</MenuItem>
                <MenuItem value="venta">Ventas</MenuItem>
                <MenuItem value="exportacion">Exportaciones</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'var(--slate-bg)' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Trayecto</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredGuias.map(g => (
                  <TableRow key={g.id} hover>
                    <TableCell sx={{ fontWeight: 800 }}>{g.codigo_guia}</TableCell>
                    <TableCell><Chip label={g.tipo} size="small" variant="outlined" /></TableCell>
                    <TableCell>{g.origen || 'Local'} → {g.destino}</TableCell>
                    <TableCell>
                      <Chip label={g.estado} size="small" color={g.estado === 'completada' ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleVerDetalles(g)}><Visibility fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ p: 4 }}><Button onClick={() => setVerGuiasDialog(false)}>Cerrar</Button></DialogActions>
      </Dialog>

      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={() => setOpenSnackbar(false)}>
        <Alert severity="success" sx={{ width: '100%', borderRadius: '12px' }}>{snackbarMessage}</Alert>
      </Snackbar>

      <style>
        {`
          @media print {
            .no-print { display: none !important; }
            body { background: white !important; padding: 20px !important; }
            .MuiPaper-root { border: 1px solid #eee !important; box-shadow: none !important; }
          }
        `}
      </style>
    </Box>
  );
};

export default Guias;
